"use client";

import { useState, useEffect } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { IoIosArrowBack, IoIosArrowForward } from "react-icons/io";
import { Star, MessageSquareQuote, Search } from "lucide-react";
import { reviewData, ReviewItemProps } from "@/data/Static/review";
import Reveal from "@/components/motion/Reveal";
import { useMotionReady } from "@/hooks/useMotionReady";

export default function Reviews() {
  const [index, setIndex] = useState(0);
  const [direction, setDirection] = useState(0);
  const ready = useMotionReady();

  const nextReview = () => {
    setDirection(1);
    setIndex((prev) => (prev + 1) % reviewData.length);
  };

  const prevReview = () => {
    setDirection(-1);
    setIndex((prev) => (prev - 1 + reviewData.length) % reviewData.length);
  };

  // Auto-play
  useEffect(() => {
    const timer = setInterval(nextReview, 8000);
    return () => clearInterval(timer);
  }, [index]);

  const currentReview = reviewData[index];

  const variants = {
    enter: (direction: number) => ({
      x: direction > 0 ? 100 : -100,
      opacity: 0,
      scale: 0.95,
    }),
    center: {
      zIndex: 1,
      x: 0,
      opacity: 1,
      scale: 1,
    },
    exit: (direction: number) => ({
      zIndex: 0,
      x: direction < 0 ? 100 : -100,
      opacity: 0,
      scale: 0.95,
    }),
  };

  return (
    <section className="relative py-20 overflow-hidden bg-linear-to-b from-background to-secondary/30">
      {/* Background elements */}
      <div className="absolute top-0 left-0 w-full h-1/2 bg-primary/5 -skew-y-3 origin-top-left -z-10" />
      
      <div className="container mx-auto px-4">
        <Reveal>
          <div className="text-center mb-12">
            <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-accent/10 border border-accent/20 text-accent text-[10px] font-black uppercase tracking-widest mb-4">
              <Star className="w-3 h-3 fill-accent" /> Vélemények
            </div>
            <h2 className="text-3xl md:text-5xl font-display font-bold text-foreground">
              Pácienseink <span className="text-primary">Visszajelzései</span>
            </h2>
            <div className="mt-4 flex items-center justify-center gap-4 text-sm text-muted-foreground font-medium">
              <div className="flex items-center gap-1">
                <Search className="w-4 h-4 text-blue-500" />
                <span>4.9/5 Google Értékelés</span>
              </div>
              <div className="w-1 h-1 rounded-full bg-border" />
              <span>150+ Elégedett Páciens</span>
            </div>
          </div>
        </Reveal>

        <div className="relative max-w-4xl mx-auto">
          {/* Main Review Card */}
          <div className="relative">
            <AnimatePresence initial={false} custom={direction} mode="wait">
              <motion.div
                key={index}
                custom={direction}
                variants={variants}
                initial={false}
                animate={ready ? "center" : { opacity: 1, x: 0, scale: 1 }}
                exit="exit"
                transition={{
                  x: { type: "spring", stiffness: 300, damping: 30 },
                  opacity: { duration: 0.2 },
                }}
                style={{
                  willChange: "transform, opacity",
                  transform: "translateZ(0)",
                  backfaceVisibility: "hidden",
                }}
                className="w-full relative py-4"
              >
                <div className="bg-card border border-border rounded-[32px] p-8 md:p-12 shadow-xl shadow-primary/5 flex flex-col items-center text-center min-h-[400px] md:min-h-[350px]">
                  <MessageSquareQuote className="w-12 h-12 text-primary/20 mb-6 shrink-0" />
                  
                  <div className="flex gap-1 text-accent mb-6">
                    {Array(currentReview.rating).fill(0).map((_, i) => (
                      <Star key={i} className="w-5 h-5 fill-accent" />
                    ))}
                  </div>

                  <p className="text-lg md:text-xl text-foreground font-medium italic leading-relaxed mb-8">
                    &quot;{currentReview.description}&quot;
                  </p>

                  <div className="mt-auto">
                    <p className="text-lg font-bold text-foreground">{currentReview.name}</p>
                    <p className="text-xs text-muted-foreground uppercase font-black tracking-widest mt-1">
                      {currentReview.source} Értékelés
                    </p>
                  </div>
                </div>
              </motion.div>
            </AnimatePresence>
          </div>

          {/* Navigation Controls */}
          <div className="flex justify-center items-center gap-6 mt-12">
            <button
              onClick={prevReview}
              className="w-12 h-12 rounded-full border border-border bg-card flex items-center justify-center text-primary transition-all hover:bg-primary hover:text-primary-foreground hover:scale-110 active:scale-95 shadow-xs"
              aria-label="Previous review"
            >
              <IoIosArrowBack size={24} />
            </button>

            <div className="flex gap-2">
              {reviewData.map((_, i) => (
                <button
                  key={i}
                  onClick={() => {
                    setDirection(i > index ? 1 : -1);
                    setIndex(i);
                  }}
                  className={`h-4 flex items-center transition-all duration-300 ${
                    i === index ? "w-8" : "w-4"
                  }`}
                  aria-label={`Go to review ${i + 1}`}
                >
                  <div className={`h-2 rounded-full w-full transition-all duration-300 ${
                    i === index ? "bg-primary" : "bg-border hover:bg-muted-foreground"
                  }`} />
                </button>
              ))}
            </div>

            <button
              onClick={nextReview}
              className="w-12 h-12 rounded-full border border-border bg-card flex items-center justify-center text-primary transition-all hover:bg-primary hover:text-primary-foreground hover:scale-110 active:scale-95 shadow-xs"
              aria-label="Next review"
            >
              <IoIosArrowForward size={24} />
            </button>
          </div>
        </div>
      </div>
    </section>
  );
}
